/**
 * DirectPaymentLinkModal.tsx
 *
 * The "Direct Payment Link" popup: pick a merchant, then copy, preview,
 * e-mail or text the patient a link to the payment portal (legacy
 * payment_link_display.php). Opened from the patient's Payments tab and from
 * a plan's Payments card; the plan card passes its planId so the link carries
 * the plan, as legacy's plan page does.
 */
import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Check, Copy, ExternalLink, Mail, MessageSquare } from 'lucide-react';
import { Button, Spinner } from '@/components/ui';
import { Modal } from '@/components/ui/Modal';
import { getPaymentLinks } from '@/services/payments.service';
import { sendEmail, sendSMS } from '@/services/communications.service';
import { formatPhone } from '@/lib/utils';

export function DirectPaymentLinkModal({
  patientId,
  patientEmail,
  patientPhone,
  patientPhoneCountryCode,
  patientName,
  planId = 0,
  open,
  onClose,
}: {
  patientId: number;
  patientEmail: string | null;
  patientPhone: string | null;
  /** The phone's stored dial code; blank formats as +1. */
  patientPhoneCountryCode?: string | null;
  patientName: string;
  /** The plan the link is for; 0 (the default) means no plan. */
  planId?: number;
  open: boolean;
  onClose: () => void;
}) {
  const [selectedDisclosure, setSelectedDisclosure] = useState<string>('');
  const [selectedMerchantId, setSelectedMerchantId] = useState<string>('');
  const [copied, setCopied] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [sendingSms, setSendingSms] = useState(false);
  const [feedback, setFeedback] = useState<string | null>(null);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['patient-payment-links', patientId, planId, selectedDisclosure],
    queryFn: () =>
      getPaymentLinks(
        patientId,
        planId,
        selectedDisclosure || undefined,
      ),
    enabled: open,
  });

  const disclosures = data?.disclosures ?? [];
  const links = data?.links ?? [];

  useEffect(() => {
    if (!open) {
      setSelectedDisclosure('');
      setSelectedMerchantId('');
      setCopied(false);
      setSendingEmail(false);
      setSendingSms(false);
      setFeedback(null);
    }
  }, [open]);

  if (!open) return null;

  const selectedLink = links.find((l) => l.merchantId === selectedMerchantId);

  function handleCopy() {
    if (!selectedLink) return;
    void navigator.clipboard.writeText(selectedLink.url).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  async function handleSendEmail() {
    if (!selectedLink || !patientEmail) return;
    if (!window.confirm('E-mail this payment link to the patient?')) return;

    setSendingEmail(true);
    setFeedback(null);
    try {
      await sendEmail({
        patientId,
        subject: 'Payment Link from Next Health',
        toEmail: patientEmail,
        toName: patientName || null,
        fromName: 'Next Health',
        htmlContent: `You have received a payment link from Next Health. Please use the following link to make a payment:<br><a href="${selectedLink.url}">${selectedLink.url}</a>`,
      });
      setFeedback('Payment link e-mailed.');
    } catch (err) {
      setFeedback(err instanceof Error ? err.message : 'Failed to send e-mail.');
    } finally {
      setSendingEmail(false);
    }
  }

  async function handleSendSms() {
    if (!selectedLink || !patientPhone) return;
    if (!window.confirm('SMS this payment link to the patient?')) return;

    setSendingSms(true);
    setFeedback(null);
    try {
      await sendSMS({
        patientId,
        toPhone: patientPhone,
        message: `You have received a payment link from Next Health. Please use the following link to make a payment:\n${selectedLink.url}`,
      });
      setFeedback('Payment link SMS sent.');
    } catch (err) {
      setFeedback(err instanceof Error ? err.message : 'Failed to send SMS.');
    } finally {
      setSendingSms(false);
    }
  }

  return (
    <Modal
      isOpen={open}
      onClose={onClose}
      title="Direct Payment Link"
      size="lg"
      footer={<Button onClick={onClose}>Close</Button>}
    >
      {isLoading ? (
        <div className="flex justify-center py-8">
          <Spinner />
        </div>
      ) : isError ? (
        <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          Failed to load payment links.
        </div>
      ) : links.length === 0 ? (
        <p className="py-4 text-sm text-slate-500">
          No active merchant accounts found. Payment links require at least one active merchant.
        </p>
      ) : (
        <div className="space-y-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-slate-700">Merchant:</label>
              <select
                value={selectedMerchantId}
                onChange={(e) => { setSelectedMerchantId(e.target.value); setCopied(false); setFeedback(null); }}
                className="rounded-md border border-slate-300 px-2 py-1 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              >
                <option value="">Select a merchant...</option>
                {links.map((l) => (
                  <option key={l.merchantId} value={l.merchantId}>
                    {l.friendlyTitle}
                  </option>
                ))}
              </select>
            </div>

            {disclosures.length > 1 && (
              <div className="flex items-center gap-2">
                <label className="text-sm font-medium text-slate-700">Disclosure:</label>
                <select
                  value={selectedDisclosure}
                  onChange={(e) => setSelectedDisclosure(e.target.value)}
                  className="rounded-md border border-slate-300 px-2 py-1 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                >
                  {disclosures.map((d) => (
                    <option key={d.disclosuresID} value={String(d.disclosuresID)}>
                      {d.name}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>

          {selectedLink && (
            <>
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  readOnly
                  value={selectedLink.url}
                  className="flex-1 rounded-md border border-slate-300 bg-slate-50 px-3 py-2 text-sm text-slate-700 select-all focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  onFocus={(e) => e.target.select()}
                />
                <button
                  type="button"
                  onClick={handleCopy}
                  className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-md border border-slate-300 bg-white text-slate-500 transition-colors hover:bg-slate-50 hover:text-slate-700"
                  title="Copy to clipboard"
                >
                  {copied ? (
                    <Check className="h-4 w-4 text-green-600" />
                  ) : (
                    <Copy className="h-4 w-4" />
                  )}
                </button>
                <a
                  href={selectedLink.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-md border border-slate-300 bg-white text-slate-500 transition-colors hover:bg-slate-50 hover:text-slate-700"
                  title="Preview in new tab"
                >
                  <ExternalLink className="h-4 w-4" />
                </a>
              </div>

              <div className="flex items-center gap-3 border-t border-slate-200 pt-4">
                <Button
                  size="sm"
                  variant="secondary"
                  leftIcon={<Mail className="h-4 w-4" />}
                  onClick={handleSendEmail}
                  disabled={!patientEmail || sendingEmail}
                  title={patientEmail ? `Email to ${patientEmail}` : 'No email on file'}
                >
                  {sendingEmail ? 'Sending...' : patientEmail ? `Email ${patientEmail}` : 'Email'}
                </Button>
                <Button
                  size="sm"
                  variant="secondary"
                  leftIcon={<MessageSquare className="h-4 w-4" />}
                  onClick={handleSendSms}
                  disabled={!patientPhone || sendingSms}
                  title={patientPhone ? `SMS to ${formatPhone(patientPhone, patientPhoneCountryCode ?? undefined)}` : 'No phone on file'}
                >
                  {sendingSms ? 'Sending...' : patientPhone ? `SMS ${formatPhone(patientPhone, patientPhoneCountryCode ?? undefined)}` : 'SMS'}
                </Button>
              </div>

              {feedback && (
                <div
                  className={`rounded-md border px-3 py-2 text-sm ${
                    feedback.includes('Failed')
                      ? 'border-red-200 bg-red-50 text-red-700'
                      : 'border-green-200 bg-green-50 text-green-700'
                  }`}
                >
                  {feedback}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </Modal>
  );
}
